/** Status and deletion: the two decisions about a project that had no button.
 *
 * Status used to move only by itself — the rollup starting a project when work began,
 * closure completing it — so "put this on hold till January" or "we are not doing this"
 * could not be said anywhere. Delete existed in the API with nothing offering it. Both are
 * Portfolio Manager and Administrator decisions, and both take a reason, because a paused,
 * cancelled or deleted project is exactly the one somebody asks about later.
 */

import { useState } from 'react'

import { Button, Can, ErrorState, Field, SectionHeader, inputClass } from '@/components/ui'
import type { Project } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import { useWorkItems } from '@/features/workbench/useWork'
import { useDeleteProject, useSetProjectStatus } from '@/features/projects/useProjects'

/** Complete is absent on purpose: completion runs through closure, which requires the
 * summary and handoff note. */
const SETTABLE = [
  { value: 'not_started', label: 'Not started' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

export function ProjectStatusControl({ project }: { project: Project }) {
  const set = useSetProjectStatus(project.id)
  const [status, setStatus] = useState(project.status)
  const [until, setUntil] = useState(project.onHoldUntil ?? '')
  const [reason, setReason] = useState('')

  if (project.status === 'complete') return null

  const changed = status !== project.status || (status === 'on_hold' && until !== (project.onHoldUntil ?? ''))

  return (
    <Can permission={Permissions.projectsTransitionStage}>
      <section>
        <SectionHeader>Status</SectionHeader>
        <div className="card space-y-3 p-4">
          <p className="text-xs text-fg-muted">
            Put the project on hold, resume it, or cancel it. Cancelled projects move to
            Archived. To finish a project, use Closure below.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Status">
              <select
                className={inputClass}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {SETTABLE.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                    {s.value === project.status ? ' — current' : ''}
                  </option>
                ))}
              </select>
            </Field>
            {status === 'on_hold' ? (
              <Field label="On hold until" hint="Optional. Nothing restarts by itself.">
                <input
                  type="date"
                  className={inputClass}
                  value={until}
                  onChange={(event) => setUntil(event.target.value)}
                />
              </Field>
            ) : null}
          </div>
          <Field label="Reason (required)" hint="Recorded in the audit trail against your name.">
            <textarea
              className={inputClass}
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          {set.error ? <ErrorState error={set.error} /> : null}
          <Button
            variant="primary"
            disabled={!changed || reason.trim().length < 3 || set.isPending}
            onClick={() =>
              set.mutate(
                {
                  status,
                  reason,
                  on_hold_until: status === 'on_hold' && until ? until : null,
                  row_version: project.rowVersion,
                },
                { onSuccess: () => setReason('') },
              )
            }
          >
            {set.isPending ? 'Saving…' : 'Change status'}
          </Button>
        </div>
      </section>
    </Can>
  )
}

export function DeleteProject({ project, onDeleted }: { project: Project; onDeleted: () => void }) {
  const remove = useDeleteProject(project.id)
  const { data: work } = useWorkItems({ projectId: project.id })
  const count = work?.items.length ?? 0
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [workItems, setWorkItems] = useState<'backlog' | 'delete'>('backlog')
  const [confirmName, setConfirmName] = useState('')

  return (
    <Can permission={Permissions.projectsDelete}>
      <section>
        <SectionHeader>Delete project</SectionHeader>
        <div className="card space-y-3 p-4">
          {!open ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-fg-muted">
                For a project that should never have been one. Finished or abandoned work is
                better closed or cancelled, which keeps it in Archived.
              </p>
              <Button variant="secondary" onClick={() => setOpen(true)}>
                Delete…
              </Button>
            </div>
          ) : (
            <>
              <fieldset className="space-y-1 text-sm">
                <legend className="micro-label mb-1 text-fg-muted">
                  Its {count} work item{count === 1 ? '' : 's'}
                </legend>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={workItems === 'backlog'}
                    onChange={() => setWorkItems('backlog')}
                  />
                  Keep them in the backlog, with no project
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={workItems === 'delete'}
                    onChange={() => setWorkItems('delete')}
                  />
                  Delete them with the project
                </label>
              </fieldset>
              <Field label="Reason (required)" hint="Recorded in the audit trail against your name.">
                <textarea
                  className={inputClass}
                  rows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
              <Field label={`Type the project name to confirm: ${project.name}`}>
                <input
                  className={inputClass}
                  value={confirmName}
                  onChange={(event) => setConfirmName(event.target.value)}
                />
              </Field>
              {remove.error ? <ErrorState error={remove.error} /> : null}
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  disabled={
                    reason.trim().length < 3 ||
                    confirmName.trim() !== project.name.trim() ||
                    remove.isPending
                  }
                  onClick={() =>
                    remove.mutate({ reason, work_items: workItems }, { onSuccess: onDeleted })
                  }
                >
                  {remove.isPending ? 'Deleting…' : 'Delete project'}
                </Button>
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </Can>
  )
}
